import type { LlmModelConfig, LlmProviderConfig } from "../../llm/config.js";
import {
  LlmTransportError,
  type LlmAdapter,
  type TextGenerationRequest,
  type TextGenerationResponse,
  type ToolGenerationRequest,
  type ToolGenerationResponse,
  type TokenUsage,
} from "../../llm/types.js";
import { addTokenUsage, withTransientRetry } from "./retry.js";

type UnknownRecord = Record<string, unknown>;

// Token accounting from an OpenAI-shaped usage object. IMPORTANT: our gateway
// (LiteLLM/aihubmix fronting Anthropic) reports Anthropic-style semantics through
// OpenAI-shaped fields — prompt_tokens is FRESH (non-cached) input only, and
// cached_tokens / cache_creation_tokens are SEPARATE additive buckets (verified: a
// response with prompt_tokens=48 carried cached_tokens=640, impossible under a subset
// model). So total input = prompt_tokens + cached + creation. cached/creation are then
// reported as subsets of that total (matching TokenUsage's contract), so downstream
// can price the cheap cache-read and pricey cache-creation portions separately.
// (A true OpenAI endpoint folds cached into prompt_tokens; if we ever add one, gate on
// provider protocol here. Today every profile routes through the Anthropic-style gateway.)
function usageOf(payload: unknown): TokenUsage {
  if (!isRecord(payload) || !isRecord(payload["usage"])) return {};
  const u = payload["usage"];
  const num = (v: unknown): number => {
    const n = typeof v === "number" ? v : 0;
    return Number.isFinite(n) ? n : 0;
  };
  const details = isRecord(u["prompt_tokens_details"]) ? u["prompt_tokens_details"] : {};
  const fresh = num(u["prompt_tokens"]);
  const cached = num(details["cached_tokens"]) || num(u["cache_read_input_tokens"]);
  const created = num(details["cache_creation_tokens"]) || num(u["cache_creation_input_tokens"]);
  return {
    inputTokens: fresh + cached + created,
    outputTokens: num(u["completion_tokens"]),
    cachedInputTokens: cached,
    cacheCreationTokens: created,
  };
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function endpoint(baseUrl: string): string {
  const base = baseUrl.trim().replace(/\/+$/, "");
  if (base.endsWith("/chat/completions")) return base;
  if (base.endsWith("/v1")) return `${base}/chat/completions`;
  return `${base}/v1/chat/completions`;
}

function firstChoice(payload: unknown): UnknownRecord | null {
  if (!isRecord(payload) || !Array.isArray(payload["choices"])) return null;
  return payload["choices"].find(isRecord) ?? null;
}

function finishReason(choice: UnknownRecord | null): string {
  return choice && typeof choice["finish_reason"] === "string" ? choice["finish_reason"] : "";
}

function message(choice: UnknownRecord | null): UnknownRecord | null {
  return choice && isRecord(choice["message"]) ? choice["message"] : null;
}

function messageText(choice: UnknownRecord | null): string {
  const content = message(choice)?.["content"];
  if (typeof content === "string") return content.trim();
  if (!Array.isArray(content)) return "";
  return content.flatMap((part) => isRecord(part) && typeof part["text"] === "string" ? [part["text"]] : []).join("\n").trim();
}

function parseJson(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    throw new LlmTransportError("invalid_response", "Provider returned invalid JSON.", { details: [raw.slice(0, 500)] });
  }
}

export class OpenAiCompatibleModel implements LlmAdapter {
  private readonly url: string;
  private readonly host: string;

  constructor(
    private readonly provider: LlmProviderConfig,
    private readonly model: LlmModelConfig,
    private readonly apiKey: string,
  ) {
    // A null baseUrl means the endpoint env var never resolved (the built-in
    // `gw` provider reads PROJECT_LITELLM_GATEWAY_BASE_URL). Name it so the
    // failure is self-diagnosing instead of a generic "request failed".
    if (!provider.baseUrl) {
      throw new LlmTransportError("endpoint", "OpenAI-compatible provider endpoint (baseUrl) is not set — the built-in gateway reads PROJECT_LITELLM_GATEWAY_BASE_URL.");
    }
    this.url = endpoint(provider.baseUrl);
    try {
      this.host = new URL(this.url).host;
    } catch {
      this.host = this.url;
    }
  }

  private async post(body: UnknownRecord, signal?: AbortSignal): Promise<{ payload: unknown; attempts: number; failedUsage: TokenUsage }> {
    const result = await withTransientRetry(this.provider.maxAttempts, async () => {
      const timeoutSignal = AbortSignal.timeout(this.provider.timeoutMs);
      const combinedSignal = signal ? AbortSignal.any([signal, timeoutSignal]) : timeoutSignal;
      let response: Response;
      try {
        response = await fetch(this.url, {
          method: "POST",
          headers: { authorization: `Bearer ${this.apiKey}`, "content-type": "application/json" },
          body: JSON.stringify(body),
          signal: combinedSignal,
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        const timeout = combinedSignal.aborted && !signal?.aborted;
        // Put the transport cause + endpoint host in the MESSAGE (not just
        // redacted details) so it survives into the per-unit error sidecar and
        // the agent can tell "host unreachable" from "auth" without debug mode.
        // The network message and host are not secrets; the api key never is.
        throw new LlmTransportError(
          timeout ? "timeout" : "request_failed",
          timeout
            ? `OpenAI-compatible request timed out reaching ${this.host}.`
            : `OpenAI-compatible request could not reach ${this.host}: ${message}`,
          { retryable: !signal?.aborted, details: [message.slice(0, 500)] },
        );
      }
      const raw = await response.text();
      if (!response.ok) {
        const retryable = response.status === 408 || response.status === 429 || response.status >= 500;
        const code = response.status === 401 || response.status === 403 ? "auth" : response.status === 429 ? "rate_limited" : "request_failed";
        throw new LlmTransportError(code, `OpenAI-compatible request to ${this.host} failed (status ${response.status}).`, {
          retryable,
          details: [`status: ${response.status}`, raw.slice(0, 500)],
        });
      }
      return parseJson(raw);
    });
    return { payload: result.value, attempts: result.attempts, failedUsage: result.failedUsage };
  }

  private baseBody(request: TextGenerationRequest): UnknownRecord {
    const messages: Array<{ role: "system" | "user"; content: string }> = [];
    if (request.system.trim()) messages.push({ role: "system", content: request.system });
    messages.push({ role: "user", content: request.user });
    return {
      model: this.model.model,
      messages,
      ...(request.responseFormat === "json_object" ? { response_format: { type: "json_object" } } : {}),
      ...(this.model.reasoning ? { reasoning_effort: this.model.reasoning } : {}),
      ...(this.model.temperature !== undefined ? { temperature: this.model.temperature } : {}),
    };
  }

  async generateText(request: TextGenerationRequest): Promise<TextGenerationResponse> {
    const { payload, attempts, failedUsage } = await this.post(this.baseBody(request), request.signal);
    const usage = addTokenUsage(failedUsage, usageOf(payload));
    try {
      const choice = firstChoice(payload);
      const reason = finishReason(choice);
      if (reason === "length") throw new LlmTransportError("truncated", "Provider output was truncated.");
      if (reason === "content_filter" || reason.startsWith("content_filter:")) throw new LlmTransportError("content_filtered", `Provider filtered the response (${reason}).`);
      const text = messageText(choice);
      if (!text) throw new LlmTransportError("empty_response", "Provider returned an empty text response.");
      return { text, finishReason: reason, attempts, usage, raw: payload };
    } catch (error) {
      if (error instanceof LlmTransportError) {
        error.attempts = attempts;
        error.usage = usage;
      }
      throw error;
    }
  }

  async generateTool(request: ToolGenerationRequest): Promise<ToolGenerationResponse> {
    const { payload, attempts, failedUsage } = await this.post({
      ...this.baseBody(request),
      tools: [{
        type: "function",
        function: { name: request.tool.name, description: request.tool.description, parameters: request.tool.inputSchema },
      }],
      tool_choice: { type: "function", function: { name: request.tool.name } },
      parallel_tool_calls: false,
    }, request.signal);
    const usage = addTokenUsage(failedUsage, usageOf(payload));
    try {
      const choice = firstChoice(payload);
      const reason = finishReason(choice);
      if (reason === "length") throw new LlmTransportError("truncated", "Provider tool output was truncated.");
      if (reason === "content_filter" || reason.startsWith("content_filter:")) throw new LlmTransportError("content_filtered", `Provider filtered the response (${reason}).`);
      const calls = message(choice)?.["tool_calls"];
      if (!Array.isArray(calls)) throw new LlmTransportError("invalid_response", "Provider did not return a tool call.");
      for (const call of calls) {
        if (!isRecord(call) || !isRecord(call["function"])) continue;
        const fn = call["function"];
        if (fn["name"] !== request.tool.name || typeof fn["arguments"] !== "string") continue;
        return { input: parseJson(fn["arguments"]), finishReason: reason, attempts, usage, raw: payload };
      }
      throw new LlmTransportError("invalid_response", "Provider did not return the required tool call.");
    } catch (error) {
      if (error instanceof LlmTransportError) {
        error.attempts = attempts;
        error.usage = usage;
      }
      throw error;
    }
  }
}
